import { APIEntity, dataManager, registerEntity, type ReceiveShowTarget } from '../APIEntity';
import { IEntity, EntityMerge } from '../IEntity';
import { ActionInfo } from '../models/ActionInfo';
import { TypeId } from '../models/TypeId';

/** null = staged only (downloaded, reviewable, NOT installed/indexed). */
export type MessageAttachmentScope = 'user' | 'project' | null;

export interface StagedFileInfo {
  path: string;
  size?: number;
  is_main?: boolean;
}

export interface StagedFilesResponse {
  files: StagedFileInfo[];
  main_file: string | null;
  /** The asset inside the staged copy, located by its type's shape (a folder
   *  type's folder, a file type's file), relative to `abs_root`; null when the
   *  copy does not hold that shape. Review opens it here. */
  asset_root: string | null;
  root: string;
  /** Absolute staged dir on the local machine (Test-it references it by path). */
  abs_root: string;
}

// The DisplayTarget shape returned by install()/setup() lives on APIEntity (it is
// generic to every entity); re-exported here for the historical import path.
export type { ReceiveShowTarget } from '../APIEntity';

/**
 * A received, staged bundle attachment awaiting explicit install.
 *
 * Minted backend-side when a message's `.flowmsg` bundle is unpacked; the
 * asset's bytes stay in the message's staging area (not indexed, not visible
 * to agents) until the user installs it via `install()`. Mirrors
 * `flow_sdk/builtin/message_attachment.py`.
 */
export interface IMessageAttachment extends IEntity {
  flow_message_id?: string;
  conversation_id?: string | null;
  /** The shared asset's identity as sent (sender-pinned id). */
  asset_type?: string;
  asset_id?: string;
  name?: string | null;
  description?: string | null;
  /** The kind a `*.value.json` file carries (its `spec_kind`), read once at stage time. */
  value_kind?: string | null;
  /** Staging path RELATIVE to the message's data dir. */
  unpacked_path?: string;
  /** Whether "Install global" (user scope) is offered — schema-derived and
   *  stamped backend-side at stage time (single source of the policy). */
  user_scope_allowed?: boolean;
  transfer_mode?: 'copy' | 'git' | 'both';
  origin?: Record<string, unknown> | null;
  scope?: MessageAttachmentScope;
  project_id?: string | null;
  installed_root?: string | null;
  installed_at?: string | null;
}

// `implements IMessageAttachment` only checks the class; it contributes no members, so every
// field declared solely on IMessageAttachment read as "does not exist". deepAssign populates
// them from the wire — this merge makes them part of the class type.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface MessageAttachment extends EntityMerge<IMessageAttachment> {}

/**
 * How a received git share comes into the receiver's own checkout — mirror of
 * `ReceivePlan` (flow_sdk/builtin/git_share_specs.py). `have`: already there;
 * `pull`: on the shared branch, behind; `cherry_pick`: `commits` (oldest first)
 * onto `branch`, also changing `other_files` outside the asset.
 */
export interface ReceivePlan {
  action: 'have' | 'pull' | 'cherry_pick';
  commit: string;
  branch: string;
  workdir: string;
  commits: string[];
  other_files: string[];
}

/** What `openShared` / `installShared` answer. */
export interface OpenSharedResult {
  show?: ReceiveShowTarget | null;
  project_id?: string;
  mismatch?: boolean;
  /** What to ask: which checkout (`choice`), clone vs copy (`placement`), replace the copy you already have
   *  (`overwrite`), or say yes to a pull / cherry-pick into your checkout (`apply`, see `plan`). */
  needs?: 'choice' | 'placement' | 'overwrite' | 'apply';
  plan?: ReceivePlan;
  candidates?: { id: string; name: string; path: string }[];
  git_share_error?: string;
  git_share_message?: string;
}

@registerEntity
export class MessageAttachment extends APIEntity<MessageAttachment> implements IMessageAttachment {
  static type: string = 'message_attachment';

  flow_message_id?: string;
  conversation_id?: string | null;
  asset_type?: string;
  asset_id?: string;
  name?: string | null;
  description?: string | null;
  unpacked_path?: string;
  user_scope_allowed?: boolean;
  transfer_mode?: 'copy' | 'git' | 'both';
  origin?: Record<string, unknown> | null;
  scope?: MessageAttachmentScope;
  project_id?: string | null;
  installed_root?: string | null;
  installed_at?: string | null;

  constructor(entity: Partial<IMessageAttachment> = {}) {
    super(entity);
    this.flow_message_id = entity.flow_message_id;
    this.conversation_id = entity.conversation_id ?? null;
    this.asset_type = entity.asset_type;
    this.asset_id = entity.asset_id;
    this.name = entity.name ?? null;
    this.description = entity.description ?? null;
    this.unpacked_path = entity.unpacked_path;
    this.user_scope_allowed = entity.user_scope_allowed ?? true;
    this.transfer_mode = entity.transfer_mode ?? 'copy';
    this.origin = entity.origin ?? null;
    // '' is the backend's CLEARED form (exclude-none saves can't null a field)
    // — normalize to null so every consumer sees one "staged" value.
    this.scope = entity.scope || null;
    this.project_id = entity.project_id || null;
    this.installed_root = entity.installed_root ?? null;
    this.installed_at = entity.installed_at ?? null;
  }

  /** Parsed TypeId of the shared asset, or null when incomplete. */
  get targetTypeId(): TypeId | null {
    if (!this.asset_type || !this.asset_id) return null;
    return new TypeId(this.asset_type, this.asset_id);
  }

  /** Canonical install-state accessor. The backend clears scope with `''`
   *  (exclude-none saves can't null a field) and WS updates can land that
   *  cleared form directly on the cached instance, bypassing the constructor —
   *  so state decisions must read THIS, never `.scope` directly. */
  get effectiveScope(): 'user' | 'project' | null {
    return this.scope || null;
  }

  get installed(): boolean {
    return this.effectiveScope != null;
  }

  /**
   * Materialize the staged asset. Copy mode: copy bytes into the chosen scope
   * root, index, and run the per-type reception setup. Git mode ("Download"):
   * clone/pull the origin and index — setup does NOT run (call {@link runSetup}
   * explicitly). Returns the DisplayTarget to navigate to (the received entity,
   * or — copy mode only — a spawned Vibe setup session), or null.
   *
   * `name` installs a received project under the receiver's own name — the
   * answer to the 409 `project_name_conflict` the backend sends when the
   * sender's name is already taken here.
   */
  async install(
    scope: 'user' | 'project',
    projectId?: string,
    opts: { overwrite?: boolean; name?: string; apply?: boolean } = {},
  ): Promise<ReceiveShowTarget | null> {
    return (await this.installShared(scope, projectId, opts)).show ?? null;
  }

  /**
   * `install`, answering in full: a git share that would write the receiver's
   * branch comes back as `needs: 'apply'` with its `plan` and writes nothing;
   * the same call with `apply: true` runs the pull / cherry-pick.
   */
  async installShared(
    scope: 'user' | 'project',
    projectId?: string,
    opts: { overwrite?: boolean; name?: string; apply?: boolean } = {},
  ): Promise<OpenSharedResult> {
    if (!this.id) throw new Error('install requires this.id');
    const action = new ActionInfo('install', MessageAttachment.type, this.id, 'POST');
    action.bodyParameters = {
      scope,
      project_id: projectId ?? null,
      overwrite: opts.overwrite ?? false,
      ...(opts.name ? { name: opts.name } : {}),
      ...(opts.apply ? { apply: true } : {}),
    };
    return (await dataManager.callAction<unknown, OpenSharedResult>(action)) ?? {};
  }

  /**
   * Run the received asset's optional setup — explicit, receiver-initiated.
   * Git-shared assets never auto-run setup on Download; the reception UI surfaces
   * a separate "Set up"/"Run" action (per ``TypeInfo.reception_verb``) that calls
   * this. Returns the DisplayTarget (a spawned setup session, or the entity when
   * the type has no setup skill), routed through ``openDisplayTarget``.
   */
  async runSetup(): Promise<ReceiveShowTarget | null> {
    if (!this.id) throw new Error('runSetup requires this.id');
    const res = await this.post<{ entity?: unknown; show?: ReceiveShowTarget | null }>('setup');
    return res?.show ?? null;
  }

  /**
   * Open a received share (Git pointer + copy) in the right place. With no
   * choice the backend picks the receiver's project; when it cannot, `needs`
   * says what to ask: `choice` (2+ checkouts, see `candidates`) or `placement`
   * (none: clone the repo, or open the copy). `git_share_error` is set when it
   * opened from the copy because Git could not open it here. `overwrite` answers
   * `needs: overwrite` — replace the copy the receiver already has. `apply`
   * answers `needs: apply` — yes to the pull / cherry-pick in `plan`.
   */
  async openShared(
    opts: { projectId?: string; choice?: 'clone' | 'copy'; overwrite?: boolean; apply?: boolean } = {},
  ): Promise<OpenSharedResult> {
    if (!this.id) throw new Error('openShared requires this.id');
    const action = new ActionInfo('open-shared', MessageAttachment.type, this.id, 'POST');
    action.bodyParameters = {
      ...(opts.projectId ? { project_id: opts.projectId } : {}),
      ...(opts.choice ? { choice: opts.choice } : {}),
      ...(opts.overwrite ? { overwrite: true } : {}),
      ...(opts.apply ? { apply: true } : {}),
    };
    return (await dataManager.callAction<unknown, OpenSharedResult>(action)) ?? {};
  }

  /** Ask the sharer for Git access to this share's repo ("Git request access" in the conversation). */
  async requestGitAccess(): Promise<void> {
    if (!this.id) throw new Error('requestGitAccess requires this.id');
    await this.post<unknown>('request-git-access');
  }

  /** Tell the sharer this conversation's project is not a checkout of the shared repo. */
  async reportShareMismatch(): Promise<void> {
    if (!this.id) throw new Error('reportShareMismatch requires this.id');
    await this.post<unknown>('report-share-mismatch');
  }

  /** Remove the installed copy (staged copy persists; chip reverts to staged). */
  async uninstall(): Promise<this> {
    if (!this.id) throw new Error('uninstall requires this.id');
    await this.post<unknown>('uninstall');
    return this;
  }

  /** List the staged files for the review modal. */
  async listStagedFiles(): Promise<StagedFilesResponse> {
    if (!this.id) throw new Error('listStagedFiles requires this.id');
    return await this.get<StagedFilesResponse>('staged-files');
  }
}
